import {Injectable} from "@angular/core";
import {AppStore} from "./app.store";
import {LocalService} from "../../shared/services/local.service";
import {catchError, EMPTY, filter, finalize, first, forkJoin, Observable, of, switchMap, tap} from "rxjs";
import {Language} from "../../shared/models/Language";
import {AppData} from "../../shared/models/AppData";
import {ResidenceService} from "../../data-access/api-residences/residence.service";
import {Residence} from "../../data-access/api-residences/Residence";
import {Country} from "../../shared/models/Country";
import {InterventionRequest} from "../../data-access/api-interventions/InterventionRequest";
import {InterventionRequestService} from "../../data-access/api-interventions/intervention.request.service";
import {AuthService} from "@auth0/auth0-angular";

@Injectable({ providedIn: 'root' })
export class AppService {

  constructor(
    private store: AppStore,
    private authService: AuthService,
    private localService: LocalService,
    private residenceService: ResidenceService,
    private interventionRequestService: InterventionRequestService,
  ) { }

  public setLoading(isLoading: boolean): void {
    this.store.setLoading(isLoading);
  }

  public init(): void {
    this.authService.isLoading$
      .pipe(
        filter((loading: boolean) => !loading),
        switchMap(() => this.authService.isAuthenticated$),
        first(),
        switchMap((isAuth: boolean) => {
          if (!isAuth) {
            this.authService.loginWithRedirect({ appState: { target: '/profile'} });
            return EMPTY;
          }
          return this.loadData$();
        }),
        finalize(() => this.setLoading(false)),
        catchError((err) => {
          console.error('[bootstrap services] init failed', err);
          return EMPTY;
        })
      ).subscribe();
  }

  private loadData$(): Observable<any> {
    return forkJoin({
      languages: this.getLanguages$(),
      countries: this.getCountries$(),
      nationalities: this.getNationalities$(),
      residences: this.getResidences$(),
    }).pipe(
      tap(() => console.log('[bootstrap services] all datasets loaded'))
    );
  }

  private getLanguages$(): Observable<any> {
    return this.localService.getAvailableLanguages()
      .pipe(
        tap((languages: Language[]) => {
          this.store.update((state: AppData) => ({
            ...state,
            languages
          }));
        }),
        catchError((error) => {
          this.store.setError(error);
          return of(error);
        })
      );
  }

  private getCountries$(): Observable<any> {
    return this.localService.getCountries()
      .pipe(
        tap((countries: Country[]) => {
          this.store.update((state: AppData) => ({
            ...state,
            countries
          }));
        }),
        catchError((error) => {
          this.store.setError(error);
          return of(error);
        })
      );
  }

  private getNationalities$(): Observable<any> {
    return this.localService.getNationalities()
      .pipe(
        tap((nationalities: string[]) => {
          this.store.update((state: AppData) => ({
            ...state,
            nationalities
          }));
        }),
        catchError((error) => {
          this.store.setError(error);
          return of(error);
        })
      );
  }

  private getResidences$(): Observable<any> {
    return this.residenceService.getAll()
      .pipe(
        tap((residences: Residence[]) => {
          this.store.update((state: AppData) => ({
            ...state,
            residences
          }));
        }),
        catchError((error) => {
          this.store.setError(error);
          return of(error);
        })
      );
  }

  public getInterventionRequests(customerId: string): void {
    this.interventionRequestService.getAllByCustomerId(customerId)
      .pipe(
        tap((interventionRequests: InterventionRequest[]) => {
          this.store.update((state: AppData) => ({
            ...state,
            interventionRequests
          }));
        }),
        catchError((error) => {
          this.store.setError(error);
          return of(error);
        })
      ).subscribe();
  }

}
